import { ThumbsDown, ThumbsUp } from 'lucide-react';

interface ApprovalModalProps {
    isOpen: boolean;
    actionType: 'accept' | 'reject' | 'rework' | null;
    comment: string;
    localError: string | null;
    onCommentChange: (comment: string) => void;
    onCancel: () => void;
    onConfirm: () => void;
}

const getModalConfig = (actionType: 'accept' | 'reject' | 'rework' | null) => {
    if (actionType === 'accept') {
        return {
            bgColor: 'bg-green-100',
            textColor: 'text-green-600',
            icon: ThumbsUp,
            title: 'อนุมัติใบงาน',
            description: 'กรุณาเพิ่มความคิดเห็นเพื่อความชัดเจน',
            label: 'ความคิดเห็น (ไม่จำเป็น)',
            buttonClass: 'bg-green-600 hover:bg-green-700',
            confirmText: 'ยืนยันการอนุมัติ'
        };
    }
    
    return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-600',
        icon: ThumbsDown,
        title: actionType === 'reject' ? 'ปฏิเสธใบงาน' : 'ส่งกลับแก้ไข',
        description: 'กรุณาระบุเหตุผลในการปฏิเสธ',
        label: 'ความคิดเห็น (จำเป็น)',
        buttonClass: 'bg-red-600 hover:bg-red-700',
        confirmText: 'ยืนยันการปฏิเสธ'
    };
};

const ApprovalModal: React.FC<ApprovalModalProps> = ({
    isOpen,
    actionType,
    comment,
    localError,
    onCommentChange,
    onCancel,
    onConfirm
}) => {
    if (!isOpen || !actionType) return null;

    const config = getModalConfig(actionType);
    const IconComponent = config.icon;
    const isCommentRequired = actionType === 'reject';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl transform transition-all duration-300 scale-100">
                <div className="text-center mb-6">
                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${config.bgColor} ${config.textColor}`}>
                        <IconComponent className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {config.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                        {config.description}
                    </p>
                </div>
                
                <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {config.label}
                    </label>
                    <textarea
                        value={comment}
                        onChange={(e) => onCommentChange(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                        rows={4}
                        placeholder="กรุณาระบุความคิดเห็น..."
                    />
                </div>
                
                {/* Error Display */}
                {localError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600 font-medium">
                            {localError}
                        </p>
                    </div>
                )}
                
                <div className="flex gap-3 justify-end">
                    <button
                        onClick={onCancel}
                        className="px-6 py-3 text-gray-600 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 font-medium transition-all duration-200"
                    >
                        ยกเลิก
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isCommentRequired && !comment.trim()}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-white shadow-md hover:shadow-lg ${config.buttonClass}`}
                    >
                        {config.confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApprovalModal;
